import React from "react";
import { Formik } from "formik";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";
import { RootState } from "us.helper/types";
import { IClosureCodesDetails } from "us.collection.admin/components/ClosureCodes/Interfaces";
import _ from "lodash";
import {
  $Input,
  $Form,
  $TextArea,
  $Radio,
  $Row,
  $Col,
  $Switch,
  $Alert,
  $FormItem,
  $Button,
  $Popconfirm,
} from "us.common/components";
import AddCloserCodeSchema from "./Validations/CloserCodesValidator";
import { ClosureCodesActions } from "us.collection.admin/constants";
import { AddClosureCode, UpdateClosureCode } from "./Repository";
import * as Actions from "us.collection.admin/actions";
import { getSelectedContent } from "../../Functions";
import { BadDebtType, ClosureCodeInitialValue } from "../../Constants";

/**
 * @description - Add/Edit Closure Code view component
 * @author Tharanga Niroshana <tharangan@unicorn-solutions.com>
 * @since 11/07/2024
 */

const ClosureCodesDetails: React.FC<IClosureCodesDetails & PropsFromRedux> = (
  props
) => {
  const { t } = useTranslation();

  const {
    onCancel,
    drawerDetails,
    saveClosureCode,
    updateClosureCode,
    closureCodeList,
  } = props;

  const { action, codeId } = drawerDetails ?? {};
  const { data } = closureCodeList ?? {};

  const handleSubmit = (values: any, actions: any) => {
    if (action == ClosureCodesActions.NEW) {
      saveClosureCode && saveClosureCode(AddClosureCode.call(values));
    }
    if (action == ClosureCodesActions.EDIT) {
      updateClosureCode && updateClosureCode(UpdateClosureCode.call(values));
    }
  };

  return (
    <Formik
      initialValues={
        action == ClosureCodesActions.EDIT
          ? getSelectedContent(data, codeId)
          : ClosureCodeInitialValue
      }
      validationSchema={AddCloserCodeSchema}
      enableReinitialize
      onSubmit={handleSubmit}
    >
      {({
        values,
        handleChange,
        handleBlur,
        handleSubmit,
        isSubmitting,
        isValidating,
        resetForm,
        ...restProps
      }: any) => (
        <>
          <$Form>
            <div>
              <div className="mb-3">
                <$Input
                  name="name"
                  label={t("US.COLLECTION.ADMIN:MANAGE_CLOSURE_CODES.NAME")}
                  size="small"
                  required
                  disabled={action == ClosureCodesActions.EDIT}
                />
              </div>
              <div className="mb-3">
                <$Input
                  name="displayName"
                  label={t(
                    "US.COLLECTION.ADMIN:MANAGE_CLOSURE_CODES.DISPLAY_NAME"
                  )}
                  size="small"
                  required
                />
              </div>
              <div className="mb-3">
                <$TextArea
                  name="description"
                  label={t(
                    "US.COLLECTION.ADMIN:MANAGE_CLOSURE_CODES.DISPLAY_NAME_ENGLISH"
                  )}
                  size="small"
                  autoSize={{ minRows: 3, maxRows: 3 }}
                  required
                  disabled={action == ClosureCodesActions.EDIT}
                />
              </div>
              <div className="mb-3">
                <div className="ant-col ant-form-item-label">
                  <label title="Description">
                    {t(
                      "US.COLLECTION.ADMIN:MANAGE_CLOSURE_CODES.BAD_DEBT_RECORD"
                    )}
                  </label>
                </div>
                <$Radio
                  name="badDebtRecord"
                  optionText="label"
                  optionValue="value"
                  style={{ marginLeft: 10 }}
                  size="small"
                  defaultValue={BadDebtType.E}
                  buttonStyle="solid"
                  optionStyle="align-items-center"
                  onChange={(e: any) => {}}
                  options={[
                    {
                      value: BadDebtType.E,
                      label: BadDebtType.E,
                    },
                    {
                      value: BadDebtType.S,
                      label: BadDebtType.S,
                    },
                  ]}
                />
              </div>
              <$Row gutter={16} className="mb-3">
                <$Col span={12}>
                  <$FormItem
                    label={t(
                      "US.COLLECTION.ADMIN:MANAGE_CLOSURE_CODES.ARCHIVE_IN"
                    )}
                    extra={t("US.COLLECTION.ADMIN:MANAGE_CLOSURE_CODES.MONTHS")}
                    required
                  >
                    <$Input name="archiveDuration" size="small" />
                  </$FormItem>
                </$Col>
                <$Col span={12}>
                  <$FormItem
                    label={t(
                      "US.COLLECTION.ADMIN:MANAGE_CLOSURE_CODES.DELETE_IN"
                    )}
                    extra={t("US.COLLECTION.ADMIN:MANAGE_CLOSURE_CODES.MONTHS")}
                    required
                  >
                    <$Input name="deletionDuration" size="small" />
                  </$FormItem>
                </$Col>
              </$Row>
              <div className="mb-2 d-flex flex-1 align-items-top">
                <div className="mr-2">
                  <$Switch
                    name="isStartFromCaseClosedDate"
                    checked={values?.isStartFromCaseClosedDate}
                    onChange={(value: boolean) => {
                      restProps.setFieldValue(
                        "isStartFromCaseClosedDate",
                        value
                      );
                    }}
                  />
                </div>
                <div style={{ marginTop: 2 }}>
                  {t(
                    "US.COLLECTION.ADMIN:MANAGE_CLOSURE_CODES.START_FROM_CASE_CLOSE_DATE"
                  )}
                </div>
              </div>
              <div>
                <$Alert
                  className="p-2"
                  message={t(
                    "US.COLLECTION.ADMIN:MANAGE_CLOSURE_CODES.START_FROM_CASE_CLOSE_DATE_MESSAGE"
                  )}
                  type="info"
                />
              </div>
            </div>
            <div className="drawer-footer-fixed align-content-center justify-content-end">
              <div>
                {(action == ClosureCodesActions.NEW || action == ClosureCodesActions.EDIT) && (
                  <$Button
                    className="mr-2"
                    disabled={!restProps.isValid}
                    onClick={handleSubmit}
                    type="primary"
                  >
                    {action == ClosureCodesActions.NEW ? t("US.COLLECTION.COMMON:COMMON.SAVE") : t("US.COLLECTION.COMMON:COMMON.UPDATE")}
                  </$Button>
                )}
                {restProps.dirty && (
                  <$Popconfirm
                    title={t(
                      "US.COLLECTION.ADMIN:MANAGE_CLOSURE_CODES.ADDNEWCANCELMSG"
                    )}
                    placement="topLeft"
                    onConfirm={() => onCancel()}
                    okText={t("US.COLLECTION.COMMON:COMMON.YES")}
                    cancelText={t("US.COLLECTION.COMMON:COMMON.NO")}
                  >
                    <$Button>{t("US.COLLECTION.COMMON:COMMON.CANCEL")}</$Button>
                  </$Popconfirm>
                )}
                {!restProps.dirty && (
                  <$Button onClick={() => onCancel()}>
                    {t("US.COLLECTION.COMMON:COMMON.CANCEL")}
                  </$Button>
                )}
              </div>
            </div>
          </$Form>
        </>
      )}
    </Formik>
  );
};

const { newClosureCode } = Actions.closureCodes;

const mapStateToProps = (state: RootState) => {
  const { closureCodes } = state;
  const { closureCodeList, drawerDetails } = closureCodes;

  return {
    closureCodeList,
    drawerDetails,
  };
};

const mapDispatchToProps = {
  saveClosureCode: newClosureCode.save,
  updateClosureCode: newClosureCode.update,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(ClosureCodesDetails);
